// Libraries
import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	IconButton,
	Typography,
} from "@mui/material";
import { useState } from "react";
import clsx from "clsx";

// Helpers
import { or } from "@/Common/helpers/sonar";

// Icons
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

// Types
import { TReconciliationType } from "../../../types/reconciliation";

// Local Constants
const COMMON_CLASSNAME = "!font-sans !text-[14.5px] !font-normal !text-default";
const INITIAL_CHECKBOX_OPTIONS = {
	New: false,
	Used: false,
};

type TReconcileNowDialogProps = {
	isOpen: boolean;
	options: { title: string; value: TReconciliationType; disabled: boolean }[];
	isPendingReconciliation: boolean;
	onCloseDialog: () => void;
	onReconcile: (value: TReconciliationType[]) => void;
};

const ReconcileNowDialog = (props: Readonly<TReconcileNowDialogProps>) => {
	const { isOpen, options, isPendingReconciliation, onCloseDialog, onReconcile } = props;
	const [checkboxOptions, setCheckboxOptions] = useState(INITIAL_CHECKBOX_OPTIONS);

	const areAllOptionsDisabled = options.every((option) => option.disabled);
	const isThereAnyCheckedOption = Object.values(checkboxOptions).some((value) => value);
	const isDisabledReconciliation = or(areAllOptionsDisabled, !isThereAnyCheckedOption);

	function handleOnClose() {
		setCheckboxOptions(INITIAL_CHECKBOX_OPTIONS);
		onCloseDialog();
	}

	function handleOnChange(value: TReconciliationType) {
		setCheckboxOptions({
			...checkboxOptions,
			[value]: !checkboxOptions[value],
		});
	}

	function handleReconcile() {
		const values = Object.entries(checkboxOptions)
			.filter(([, checked]) => checked)
			.map(([value]) => value as TReconciliationType);

		onReconcile(values);

		const resetStates = values.reduce(
			(acc, key) => {
				return { ...acc, [key]: false };
			},
			{ ...checkboxOptions }
		);

		setCheckboxOptions(resetStates);
	}

	return (
		<Dialog
			classes={{
				paper: "!rounded-[10px] !shadow-md",
			}}
			open={isOpen}
			onClose={handleOnClose}
			PaperProps={{
				style: {
					width: "330px",
				},
			}}
			disableEscapeKeyDown
		>
			<DialogTitle className="!px-6 !pt-6 !pb-3">
				<Typography className={COMMON_CLASSNAME}>Please select what data you would like to reconcile:</Typography>
				<IconButton className="!absolute !right-1 !top-1 !text-default" aria-label="close" onClick={handleOnClose}>
					<CloseRoundedIcon fontSize="small" />
				</IconButton>
			</DialogTitle>
			<DialogContent>
				{options.map((option) => (
					<FormControlLabel
						key={`reconcile-now-dialog-${option.value}`}
						control={
							<Checkbox
								className="!p-1"
								checked={checkboxOptions[option.value] && !option.disabled}
								disabled={option.disabled}
								onChange={() => handleOnChange(option.value)}
							/>
						}
						label={
							<Typography className={clsx({ [`${COMMON_CLASSNAME}`]: true, "!text-disabled": option.disabled })}>
								{option.title}
							</Typography>
						}
						labelPlacement="end"
						className="!ml-[-6px]"
						onChange={(e) => e.preventDefault()}
					/>
				))}

				<Box className="!mt-3 !flex !justify-end">
					<Button
						variant="contained"
						className={clsx({
							"h-[30px] w-[82px] !shadow-none !rounded-[6px] hover:!shadow-primary-shadow-50": true,
							"!bg-primary": !or(isDisabledReconciliation, isPendingReconciliation),
							"!bg-disabled !text-white": or(isDisabledReconciliation, isPendingReconciliation),
						})}
						onClick={handleReconcile}
						disabled={or(isDisabledReconciliation, isPendingReconciliation)}
					>
						{isPendingReconciliation ? (
							<CircularProgress color="inherit" size={20} />
						) : (
							<Typography className="select-plan-dialog-assign-btn-text">Reconcile</Typography>
						)}
					</Button>
				</Box>
			</DialogContent>
		</Dialog>
	);
};

export default ReconcileNowDialog;
